/**
 * The workflow item list request, shared by `useWorkflowItems` and the inbox's
 * bulk actions, which load the complete set of a view on demand.
 */

import { WorkflowItemsService } from "@/api/services/WorkflowItemsService";
import type { WorkflowItemListParams } from "@/types/workflow";

/** The API caps one page at 1000 rows; larger limits are ignored server-side. */
const MAX_PAGE_SIZE = 1000;

/**
 * The list endpoint's filter and view arguments after limit and offset, in
 * the generated client's order. `undefined` leaves a parameter out.
 */
function viewArgs(params: WorkflowItemListParams) {
  return [
    params.category,
    params.state,
    params.assignedRole,
    params.assignedTo,
    params.pending || undefined,
    params.mine || undefined,
    undefined, // lead: the group lead's inbox of the employee app
    params.overdue || undefined,
    params.dueToday || undefined,
    params.unassigned || undefined,
    params.completed || undefined,
    params.q || undefined,
    params.sort,
  ] as const;
}

/**
 * One page of workflow items, or with `fetchAll` the complete set: pages are
 * accumulated until `total_count` rows are in, so the tail is never cut off.
 */
export async function fetchWorkflowItems(params: WorkflowItemListParams) {
  if (params.fetchAll) {
    const first = await WorkflowItemsService.getApiV1WorkflowItems(MAX_PAGE_SIZE, 0, ...viewArgs(params));
    const total = first.total_count ?? first.items?.length ?? 0;
    const items = [...(first.items ?? [])];
    for (let off = MAX_PAGE_SIZE; off < total; off += MAX_PAGE_SIZE) {
      const page = await WorkflowItemsService.getApiV1WorkflowItems(MAX_PAGE_SIZE, off, ...viewArgs(params));
      items.push(...(page.items ?? []));
    }
    return { ...first, items, total_count: total };
  }
  return WorkflowItemsService.getApiV1WorkflowItems(params.limit ?? 20, params.offset, ...viewArgs(params));
}
